import React, { useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from '@tmapy/redux';

import { OrderAttachmentsSection } from '../../app/components/OrderAttachmentsSection';
import type {
  OrderAttachmentItem,
  OrderAttachmentUploadResult,
  UploadProgressHandler,
} from '../../app/components/OrderAttachmentsSection';
import { ordersRetrieveOperation } from '../../openapi/operations/ordersRetrieve';
import { getLUID } from '../../utils/luid';
import { actionAddAttachment, actionDeleteAttachment } from '../actions/newOrder';
import type { AttachmentProps } from '../actions/newOrder';

/** Objednávka posílá přílohy jako base64 v těle požadavku, ne samostatným nahráním. */
const readAttachmentFile = (
  file: File,
  onProgress: UploadProgressHandler,
): Promise<AttachmentProps> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = ({ loaded, total }) => onProgress({ loaded, total });
    reader.onload = () => {
      const dataURL = reader.result as string;
      resolve({ luid: getLUID(), name: file.name, file: dataURL.slice(dataURL.indexOf(',') + 1) });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const toSectionItem = ({ id, luid, name, created }: AttachmentProps): OrderAttachmentItem => ({
  id: id ?? luid ?? name,
  name,
  created,
  isPending: id === undefined,
});

export const NewOrderAttachments: React.FC = () => {
  const dispatch = useDispatch();
  const { id: orderId, attachments } = useSelector((state) => state.newOrder);
  const items = useMemo(() => attachments.map(toSectionItem), [attachments]);
  // Při úpravě objednávky jdou její už uložené přílohy stáhnout.
  const resourceUrl = orderId
    ? `${globalThis.DMX_CONFIG.server}${ordersRetrieveOperation({ id: Number(orderId) }).path}`
    : null;

  const handleUpload = useCallback(
    async (
      file: File,
      onUploadProgress: UploadProgressHandler,
    ): Promise<OrderAttachmentUploadResult> => {
      try {
        const attachment = await readAttachmentFile(file, onUploadProgress);
        dispatch(actionAddAttachment(attachment));
        return { ok: true, data: toSectionItem(attachment) };
      } catch (error) {
        console.error(`File ${file.name}:`, error);
        return { ok: false };
      }
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    async (key: OrderAttachmentItem['id']) => {
      dispatch(actionDeleteAttachment(key));
      return { ok: true };
    },
    [dispatch],
  );

  return (
    <OrderAttachmentsSection
      resourceUrl={resourceUrl}
      attachments={items}
      onUpload={handleUpload}
      onDelete={handleDelete}
    />
  );
};
